/**
 * Team-Verwaltung (Spec 7.3 / 8.1): anlegen, umbenennen, Mitglieder pflegen,
 * loeschen.
 *
 * Mitglieder werden **komplett ersetzt** — dieselbe Regel wie im Service
 * (`replaceTeamMembers`). Ein Teil-Update waere im UI eine zweite Wahrheit
 * darueber, wer gerade im Team ist.
 */
import type { Team, TenantMemberDetail } from '@tickethub/shared';
import { Trash2, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { membersApi, teamsApi } from '@/lib/tickets';

export function TeamsPage() {
  const { t } = useTranslation();
  const { activeMembership } = useSession();
  const tenantId = activeMembership?.tenant_id ?? null;
  const isAdmin = activeMembership?.role === 'admin';

  const [teams, setTeams] = useState<Team[] | null>(null);
  const [members, setMembers] = useState<TenantMemberDetail[]>([]);
  /** Mitglieder je Team — eine Anfrage je Team, es sind wenige. */
  const [teamMembers, setTeamMembers] = useState<Record<string, string[]>>({});
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [teamResult, memberResult] = await Promise.all([
        teamsApi.list(tenantId),
        membersApi.list(tenantId),
      ]);
      setTeams(teamResult.items);
      setMembers(memberResult.items);

      const entries = await Promise.all(
        teamResult.items.map(async (team) => {
          const result = await teamsApi.members(tenantId, team.id);
          return [team.id, result.items] as const;
        }),
      );
      setTeamMembers(Object.fromEntries(entries));
    } catch (error) {
      setTeams([]);
      toastError(error, t);
    }
  }, [tenantId, t]);

  useEffect(() => {
    // `reload` setzt State erst *nach* dem Netzwerk-Await — es entsteht keine
    // kaskadierende Render-Schleife. Die Regel kann das nicht unterscheiden.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void reload();
  }, [reload]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    try {
      await action();
      await reload();
    } catch (error) {
      toastError(error, t);
    } finally {
      setBusy(false);
    }
  }

  function toggleMember(team: Team, memberId: string) {
    const current = teamMembers[team.id] ?? [];
    const next = current.includes(memberId)
      ? current.filter((id) => id !== memberId)
      : [...current, memberId];
    void run(() => teamsApi.update(tenantId ?? '', team.id, { member_ids: next }));
  }

  if (!activeMembership) {
    return (
      <Alert>
        <AlertDescription>{t('tenant.empty')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('team.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('team.intro')}</p>
      </div>

      {isAdmin ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim().length === 0) return;
            void run(async () => {
              await teamsApi.create(tenantId ?? '', { name: name.trim(), member_ids: [] });
              setName('');
            });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="team-name">{t('team.name')}</Label>
            <Input
              id="team-name"
              value={name}
              placeholder={t('team.name_placeholder')}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || name.trim().length === 0}>
            <Users aria-hidden="true" />
            {t('team.create')}
          </Button>
        </form>
      ) : null}

      {teams === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : teams.length === 0 ? (
        <Alert>
          <AlertDescription>{t('team.empty')}</AlertDescription>
        </Alert>
      ) : (
        <ul className="space-y-3">
          {teams.map((team) => {
            const selected = teamMembers[team.id] ?? [];
            return (
              <li key={team.id} className="space-y-3 rounded-lg border p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-medium">{team.name}</h2>
                  <span className="text-xs text-muted-foreground">
                    {t('team.member_count', { count: selected.length })}
                  </span>
                  {isAdmin ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      disabled={busy}
                      onClick={() => void run(() => teamsApi.remove(tenantId ?? '', team.id))}
                    >
                      <Trash2 aria-hidden="true" />
                      {t('common.delete')}
                    </Button>
                  ) : null}
                </div>

                {members.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('team.no_members_available')}</p>
                ) : (
                  <ul className="flex flex-wrap gap-3">
                    {members.map((member) => (
                      <li key={member.id}>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            disabled={!isAdmin || busy}
                            checked={selected.includes(member.id)}
                            onChange={() => toggleMember(team, member.id)}
                          />
                          {member.user.name}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
